import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormGroup, Modal, ModalBody } from 'reactstrap';
import { Button } from '@shoflo/polymer/components/Button/Button';
import TemplateItem from '@shoflo/polymer/components/TemplateChooser/TemplateItem';
import createFromTemplate from '@shoflo/studio-redux/actions/template/createFromTemplate';
import { selectLastMediaSource } from '@shoflo/studio-redux/selectors/broadcastSourcesSelector';
import { useCommonPageActionsApi } from '@studio/metrics/client/react/useCommonPageActionsApi';

import { MEDIA_PICKER } from 'constants/Modal';
import useModal from 'hooks/useModal';
import {
  StyledCloseButton,
  StyledJustifyRight,
  StyledModalHeader,
  StyledWrapper
} from './MediaPicker.styled';

function MediaPicker() {
  const dispatch = useDispatch();
  const { stockContentUsed } = useCommonPageActionsApi();

  const [templateName, setTemplateName] = useState('');
  const { isOpen, clear } = useModal(MEDIA_PICKER);
  const isAddButtonDisabled = !templateName;
  const { order: lastMediaOrder = 0 } = useSelector(selectLastMediaSource);
  const { templates: templateMedia, isLoading } = useSelector(
    ({ templates }) => templates
  );

  function handleChange(e) {
    const { value } = e.target;
    setTemplateName(value);
  }

  function handleAdd(e) {
    e.preventDefault();
    // using template name. get the object and send object
    const selectedTemplate = templateMedia.find(
      template =>
        template.key === templateName || template.name === templateName
    );

    onSelectTemplate({
      ...selectedTemplate,
      isEventTemplate: false,
      media: null
    });

    stockContentUsed({
      templateName
    });
  }

  function onSelectTemplate(template) {
    const payload = {
      _id: template._id,
      title: template.name || template.key,
      order: lastMediaOrder + 1,
      value: { eventDocId: template._id, url: template.url }
    };

    dispatch(
      createFromTemplate({
        ...payload,
        type: 'media',
        value: template.value,
        isEventTemplate: template.isEventTemplate
      })
    );

    clear();
  }

  if (!isOpen) return null;

  const wrapperId = 'template-chooser-container';
  const containerElementSelector = `#${wrapperId}`;

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      toggle={clear}
      data-testid="media-template-chooser-modal"
    >
      <StyledModalHeader>
        Add stock content
        <p>
          Take your presentation to the next level by adding animations,
          countdowns, and more.
        </p>
      </StyledModalHeader>
      <ModalBody>
        <StyledWrapper id={wrapperId}>
          {!isLoading
            ? templateMedia.map((item, index) => (
                <TemplateItem
                  item={item}
                  index={index}
                  key={index}
                  title={getMediaTitle(item)}
                  handleChange={handleChange}
                  containerElementSelector={containerElementSelector}
                />
              ))
            : null}
        </StyledWrapper>

        <Form data-testid="templateChooserForm" onSubmit={handleAdd}>
          <FormGroup>
            <div className="float-end" style={{ display: 'flex' }}>
              <Button
                data-testid="chooseTemplate"
                disabled={isAddButtonDisabled}
              >
                Save
              </Button>

              <StyledJustifyRight>
                <StyledCloseButton
                  data-testid="closeButton"
                  color="light"
                  variant="outlined"
                  onClick={e => {
                    e.preventDefault();
                    clear();
                  }}
                >
                  Close
                </StyledCloseButton>
              </StyledJustifyRight>
            </div>
          </FormGroup>
        </Form>
      </ModalBody>
    </Modal>
  );
}

function getMediaTitle(item) {
  const filename = item.name || item.key?.split('/').pop() || '';
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.slice(0, lastDot) : filename;
}

export default MediaPicker;
